import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { UseFormReturn } from "react-hook-form";
import { ToolFormValues } from "@/types";
import { RichTextEditor } from "@/components/RichTextEditor";
import { useState } from "react";
import { Loader2, Wand2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface AdditionalInfoSectionProps {
  form: UseFormReturn<ToolFormValues>;
}

export const AdditionalInfoSection = ({ form }: AdditionalInfoSectionProps) => {
  const [isGenerating, setIsGenerating] = useState(false);

  const generateUsageGuide = async () => {
    const name = form.getValues("name");
    const description = form.getValues("description");

    if (!name || !description) {
      toast.error("يجب إدخال اسم ووصف الأداة أولاً");
      return;
    }

    setIsGenerating(true);
    try {
      console.log('Calling generate-usage-guide function with:', { name, description });
      
      const { data, error } = await supabase.functions.invoke('generate-usage-guide', {
        body: { name, description }
      });

      if (error) {
        console.error('Supabase function error:', error);
        throw error;
      }
      
      if (!data?.guide) {
        throw new Error('لم يتم استلام دليل الاستخدام');
      }

      console.log('Received guide:', data.guide);
      form.setValue("usage_guide", data.guide);
      toast.success("تم توليد دليل الاستخدام بنجاح");
    } catch (error) {
      console.error("Error generating usage guide:", error);
      toast.error("حدث خطأ أثناء توليد دليل الاستخدام");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      <FormField
        control={form.control}
        name="usage_guide"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-lg font-semibold text-gray-900 flex items-center justify-between">
              <span>دليل الاستخدام</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={generateUsageGuide}
                disabled={isGenerating}
                className="gap-2"
              >
                {isGenerating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Wand2 className="h-4 w-4" />
                )}
                <span>توليد دليل باستخدام الذكاء الاصطناعي</span>
              </Button>
            </FormLabel>
            <FormControl>
              <RichTextEditor
                value={field.value || ''}
                onChange={field.onChange}
                placeholder="اكتب دليلاً مفصلاً لكيفية استخدام الأداة..."
              />
            </FormControl>
            <FormMessage className="text-red-500 text-sm" />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="tutorial_video_url"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-lg font-semibold text-gray-900">رابط الفيديو التعليمي</FormLabel>
            <FormControl>
              <Input 
                {...field} 
                className="text-right text-gray-900 bg-white border-2 border-gray-200 focus:border-primary focus:ring-primary" 
                placeholder="أدخل رابط الفيديو التعليمي..."
                dir="ltr"
              />
            </FormControl>
            <FormMessage className="text-red-500 text-sm" />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="is_featured"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border-2 border-gray-200 p-4 shadow-sm rtl:space-x-reverse">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
                className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
              />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-lg font-semibold text-gray-900">
                أداة مميزة
              </FormLabel>
            </div>
          </FormItem>
        )}
      />
    </div>
  );
};